import { PHASE_PRODUCTION_BUILD } from 'next/constants';

import { queuedFetch } from '../utils/requestQueue';

import { API_ENDPOINTS } from '@/shared/api/endPoints';

const RETRIES = 3;
const RETRY_DELAY_MS = 1000;

interface GetCasesPayload {
  limit: string;
  offset: string;
  products?: string[];
  industries?: string[];
}

export interface GetCasesResponse {
  items: Case[];
}

export interface Case {
  id: number;
  title: string;
  description: string;
  img: string;
  imgHeight?: number;
  products: string[];
  industries: string[];
  url?: string;
}

export default async function getCases({
  products,
  industries,
  limit,
  offset,
}: GetCasesPayload): Promise<GetCasesResponse> {
  const searchParams = new URLSearchParams({ limit, offset });
  products?.forEach((product) => searchParams.append('products', product));
  industries?.forEach((industry) =>
    searchParams.append('industries', industry),
  );

  const url = `${process.env.NEXT_PUBLIC_API_URL}${API_ENDPOINTS.CASES_URL}?${searchParams}`;

  let lastError = '';

  for (let attempt = 1; attempt <= RETRIES; attempt += 1) {
    try {
      const data = await queuedFetch(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (data.ok) {
        return data.json();
      }

      lastError = `${data.status} ${data.statusText}`;
    } catch (error) {
      lastError = error instanceof Error ? error.message : String(error);
    }

    console.warn(`[getCases] attempt ${attempt}/${RETRIES} failed:`, {
      url,
      error: lastError,
    });

    if (attempt < RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    }
  }

  // Статические страницы рендерятся при сборке: пустой ответ молча уехал бы
  // в образ на весь revalidate. Пусть лучше упадёт сборка
  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
    throw new Error(`[getCases] request failed during build: ${lastError}`);
  }

  return { items: [] };
}
